import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngxs/store';
import { from } from 'rxjs';
import { ChangesToHeadsetState } from './changes-to-headset-state.enum';
import { AbstractSamwinHeadsetManufacturerService } from './headset-manufacturer-service.abstract';
import { HeadsetManufacturerType } from './headset-manufacturer-type';
import * as bowser from 'bowser';
import { addEventListener, init, shutdown, mute, unmute, onHook, offHook, ring, hold, resume, getDevices } from '@gnaudio/jabra-browser-integration';
import { HeadsetDevice } from './headset-device';
import _ from 'lodash';

@Injectable({
   providedIn: 'root',
})
export class JabraService extends AbstractSamwinHeadsetManufacturerService implements OnDestroy {
   private deviceList: Array<HeadsetDevice>;
   private isChrome: boolean;
   private isInitialized: boolean;

   constructor(store: Store) {
      super(store, HeadsetManufacturerType.JABRA);
      this.deviceList = new Array<HeadsetDevice>();
      this.isChrome = bowser.getParser(window.navigator.userAgent).getBrowserName() === 'Chrome';
   }

   protected initializeSdk(): void {
      if (this.isChrome) {
         from(init()).subscribe(
            (val) => {
               this.isInitialized = true;
               console.log(`Jabra SDK initialization successful`);
               this.registerEventHandlers();
               this.publishDeviceList();
            },
            (err) => {
               console.error('Jabra SDK initialization went wrong!', err);
            }
         );
      }
   }

   protected terminateSdk(): void {
      if (!this.isChrome || !this.isInitialized) {
         return;
      }
      from(shutdown()).subscribe(
         (val) => {
            this.isInitialized = false;
            console.log(`Jabra SDK de-initialization successful`);
         },
         (err) => {
            console.error('Jabra SDK de-initialization went wrong!', err);
         }
      );
   }

   private registerEventHandlers() {
      addEventListener('device attached', (event) => {
         this.publishDeviceList();
      });

      addEventListener('device detached', (event) => {
         this.publishDeviceList();
      });

      addEventListener('mute', (event) => {
         this.toggleMute();
      });

      addEventListener('unmute', (event) => {
         this.toggleMute();
      });

      addEventListener('acceptcall', (event) => {
         this.acceptIncomingCall();
      });

      addEventListener('endcall', (event) => {
         this.endCall();
      });
   }

   public changeHeadsetState(change: ChangesToHeadsetState): void {
      if (this.deviceList.length > 0) {
         console.debug(`Changing Jabra headset state to '${change}'`);
         switch (change) {
            case ChangesToHeadsetState.MuteHeadset:
               mute();
               break;
            case ChangesToHeadsetState.UnmuteHeadset:
               unmute();
               break;
            case ChangesToHeadsetState.OnHook:
               onHook();
               break;
            case ChangesToHeadsetState.OffHook:
               offHook();
               break;
            case ChangesToHeadsetState.IncomingCall:
               ring();
               break;
            case ChangesToHeadsetState.OutgoingCall:
               offHook();
               break;
            case ChangesToHeadsetState.Hold:
               hold();
               break;
            case ChangesToHeadsetState.Resume:
               resume();
               break;
         }
      }
   }

   private publishDeviceList() {
      console.debug(`Publishing device list`);
      from(getDevices()).subscribe((devices: any) => {
         const deviceList = _.map(devices, (device) => {
            return <HeadsetDevice>{
               id: device.deviceID,
               manufacturer: this.targetHeadsetManufacturer,
               name: device.deviceName,
            };
         });
         console.debug(`Published list of attached Jabra devices: ${JSON.stringify(deviceList)}`);
         this.deviceList = deviceList;
         this.attachedHeadset$.next(deviceList);
         if (this.deviceList.length > 0) {
            this.bringDeviceUpToState();
         }
      });
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }
}
